import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';

import { GOAL_MEASUREMENT_TYPES } from 'constants/ProjectConstants';
import { roundOffToPlaces } from 'constants/helper';

import Caption from 'uiToolkitV1/Caption';
import { InputV2 } from 'uiToolkitV1/InputV2';
import Label from 'uiToolkitV1/Label';
import { Tooltip } from 'uiToolkitV1/Tooltip';

const ERROR_MSG = 'This is a mandatory field';

const InputLabelWithTooltip = ({
  children,
  labelText,
  labelIcon,
  captionText = '',
  captionVariant = 'default',
  containerProps = {},
  labelProps = {},
}) => (
  <Box px={3} py={1.5} {...containerProps}>
    <Tooltip label={labelText}>
      <Box width="100%">
        <Label Icon={labelIcon} mb={1} {...labelProps}>
          {labelText}
        </Label>
      </Box>
    </Tooltip>
    {children}
    {captionText.length > 0 ? <Caption variant={captionVariant}>{captionText}</Caption> : null}
  </Box>
);

export const OKRAchievedValue = ({
  measure = GOAL_MEASUREMENT_TYPES[1].code,
  measureType = '',
  achieved = 0,
  onChange = () => {},
  isReadOnly = false,
  readOnlyReason = '',
  /* autosave will trigger onChange on every small change */
  enableAutosave = false,
  labelProps = {},
  containerProps = {},
}) => {
  const ref = useRef(null);
  const [localValue, setLocalValue] = useState(achieved);
  const [error, setError] = useState('');

  const hasError = error.length > 0;

  const safeOnChange = () => {
    if (hasError) return;
    const convertedLocalValue = `${roundOffToPlaces(localValue, 2)}`;
    setLocalValue(convertedLocalValue);
    if (Number(achieved) === Number(convertedLocalValue)) return;

    onChange(convertedLocalValue);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter' && !hasError) {
      ref?.current?.blur();
    }
  };

  const handleLocalValueChange = (ev) => {
    const { target } = ev;
    const isEmpty = target?.value?.trim()?.length === 0;

    setError(isEmpty ? ERROR_MSG : '');

    if (measure === GOAL_MEASUREMENT_TYPES[1].code) {
      const regex = /^[0-9-\b]+$/g;
      if (!regex.test(target.value)) {
        if (!target.value) {
          setLocalValue(target.value);
        } else if (target.value.includes('.')) {
          setLocalValue(target.value);
        } else {
          setError('Only numbers are allowed');
          return;
        }
      }
      setLocalValue(target.value);
      if (enableAutosave) {
        onChange(target.value, isEmpty);
      }
      return;
    }

    setLocalValue(target.value);

    if (enableAutosave) {
      onChange(target.value, isEmpty);
    }
  };

  /* Sync with prop value */
  useEffect(() => {
    setLocalValue(achieved);
  }, [achieved]);

  return (
    <InputLabelWithTooltip
      labelText={`Achieved ${measureType ?? ''}`}
      containerProps={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ...containerProps }}
      captionText={error}
      captionVariant="error"
      labelProps={labelProps}
    >
      <Tooltip label={readOnlyReason} shouldWrapChildren>
        <InputV2
          ref={ref}
          isInvalid={hasError}
          isReadOnly={isReadOnly}
          type="number"
          isDashed
          value={localValue}
          onChange={handleLocalValueChange}
          onKeyDown={onKeyDown}
          onBlur={safeOnChange}
        />
      </Tooltip>
    </InputLabelWithTooltip>
  );
};
